import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, Sparkles, BookOpen, Mic, Users, ChevronRight } from 'lucide-react';
import { useContent } from '../context/ContentContext';

const iconMap = {
  Target: Target,
  Sparkles: Sparkles,
  BookOpen: BookOpen,
  Mic: Mic,
  Users: Users,
};

const pillarDetails = {
  Clarity: "Master the art of structuring your thoughts for maximum impact. Learn frameworks for organizing complex ideas into compelling narratives.",
  Presence: "Develop the gravitas that commands attention. Learn to project confidence through body language, voice, and energy management.",
  Story: "Transform data and facts into memorable stories. Learn the neuroscience of storytelling and emotional connection.",
  "Voice & Delivery": "Master vocal variety, strategic pauses, and dynamic delivery. Your voice is an instrument—learn to play it.",
  Influence: "Understand the psychology of persuasion. Learn ethical influence techniques that build trust and drive action.",
};

const Framework = () => {
  const { frameworkContent } = useContent();
  const [activePillar, setActivePillar] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <section id="framework" className="py-24 bg-[var(--bg-section)] relative overflow-hidden noise-overlay" data-testid="framework-section">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Floating orbs */}
        <motion.div
          animate={{
            y: [0, -30, 0],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-[var(--accent-teal)] blur-[120px] opacity-20"
        />
        <motion.div
          animate={{
            y: [0, 30, 0],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full bg-[var(--accent-gold)] blur-[120px] opacity-20"
        />
        
        {/* Animated circles */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px]">
          <motion.div 
            animate={{ rotate: 360 }}
            transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
            className="absolute inset-0 rounded-full border border-[var(--border-accent)]/30"
          />
          <motion.div 
            animate={{ rotate: -360 }}
            transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
            className="absolute inset-[100px] rounded-full border border-[var(--accent-teal)]/20"
          />
          <motion.div 
            animate={{ rotate: 360 }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            className="absolute inset-[200px] rounded-full border border-[var(--accent-gold)]/20"
          />
        </div>
      </div>

      <div className="container-main relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <motion.span 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block text-base font-medium text-[var(--accent-gold)] uppercase tracking-widest mb-4 px-4 py-2 rounded-full border border-[var(--accent-gold)]/30 bg-[var(--accent-gold)]/5"
          >
            The Success Blueprint
          </motion.span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--text-primary)] mb-4">
            The <span className="gradient-text-gold">Magnetic Leadership Communicator</span> Framework
          </h2>
          <p className="text-lg text-[var(--text-muted)] max-w-2xl mx-auto">
            {frameworkContent.subheadline}
          </p>
        </motion.div>

        {/* Framework Pillars - Interactive Grid */}
        <div className="grid md:grid-cols-5 gap-4 lg:gap-6 mb-8">
          {frameworkContent.pillars.map((pillar, index) => {
            const Icon = iconMap[pillar.icon];
            const isActive = activePillar === index;
            const isHovered = hoveredIndex === index;
            
            return (
              <motion.div
                key={pillar.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onClick={() => setActivePillar(isActive ? null : index)}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                className={`relative cursor-pointer group ${isActive ? 'z-20' : 'z-10'}`}
                data-testid="framework-pillar-item"
              >
                <motion.div
                  animate={{
                    scale: isActive ? 1.05 : 1,
                    y: isHovered ? -8 : 0,
                  }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className={`interactive-card p-6 text-center h-full shimmer-border ${
                    isActive 
                      ? 'border-[var(--accent-teal)] shadow-lg shadow-[var(--accent-teal)]/20' 
                      : ''
                  }`}
                >
                  {/* Step Badge */}
                  <motion.div 
                    className={`inline-flex items-center gap-1 text-xs font-bold mb-4 px-3 py-1 rounded-full ${
                      isActive 
                        ? 'bg-[var(--accent-teal)] text-white' 
                        : 'bg-[var(--accent-teal)]/10 text-[var(--accent-teal)]'
                    }`}
                    whileHover={{ scale: 1.05 }}
                  >
                    STEP {index + 1}
                  </motion.div>
                  
                  {/* Icon with glow */}
                  <motion.div
                    whileHover={{ rotate: 15, scale: 1.1 }}
                    animate={{ 
                      boxShadow: isActive 
                        ? '0 0 30px rgba(20, 184, 166, 0.5)' 
                        : '0 0 0px rgba(20, 184, 166, 0)'
                    }}
                    className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                      isActive
                        ? 'bg-gradient-to-br from-[var(--accent-teal)] to-[var(--accent-blue)]'
                        : 'bg-gradient-to-br from-[var(--accent-teal)]/20 to-[var(--accent-blue)]/20 group-hover:from-[var(--accent-teal)] group-hover:to-[var(--accent-blue)]'
                    }`}
                  >
                    <Icon className={`w-8 h-8 transition-colors duration-300 ${
                      isActive ? 'text-white' : 'text-[var(--accent-teal)] group-hover:text-white'
                    }`} />
                  </motion.div>

                  {/* Title */}
                  <h3 className={`font-display text-lg font-semibold mb-3 transition-colors duration-300 ${
                    isActive ? 'text-[var(--accent-teal)]' : 'text-[var(--text-primary)]'
                  }`}>
                    {pillar.name}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-[var(--text-muted)] leading-relaxed">
                    {pillar.description}
                  </p>

                  {/* Expand indicator */}
                  <motion.div 
                    animate={{ rotate: isActive ? 90 : 0 }}
                    className="mt-4 flex items-center justify-center gap-1 text-xs text-[var(--accent-teal)]"
                  >
                    <span>{isActive ? 'Less' : 'Learn more'}</span>
                    <ChevronRight className="w-3 h-3" />
                  </motion.div>
                </motion.div>

                {/* Connector line to next */}
                {index < 4 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-[var(--border-accent)] to-transparent transform -translate-y-1/2 z-0" />
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Expanded Detail Panel */}
        <AnimatePresence>
          {activePillar !== null && (
            <motion.div
              initial={{ opacity: 0, height: 0, y: -20 }}
              animate={{ opacity: 1, height: 'auto', y: 0 }}
              exit={{ opacity: 0, height: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="interactive-card p-8 mt-4 border-[var(--accent-teal)]/30">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[var(--accent-teal)] to-[var(--accent-blue)] flex items-center justify-center flex-shrink-0">
                    {(() => {
                      const Icon = iconMap[frameworkContent.pillars[activePillar].icon];
                      return <Icon className="w-8 h-8 text-white" />;
                    })()}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-display text-xl font-semibold text-[var(--accent-teal)] mb-2">
                      {frameworkContent.pillars[activePillar].name}
                    </h4>
                    <p className="text-[var(--text-secondary)] leading-relaxed">
                      {pillarDetails[frameworkContent.pillars[activePillar].name]}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {['Workshops', 'Coaching', 'Practice Sessions'].map((tag) => (
                        <span key={tag} className="text-xs px-3 py-1 rounded-full bg-[var(--accent-teal)]/10 text-[var(--accent-teal)] border border-[var(--accent-teal)]/20">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Framework;
